import { dropRight } from 'lodash'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

type HistoryTrackFnArgs = {
    excluding: RegExp
}

type HistoryTrackerContextType = {
    overrideLastEntryPageTitle: (title: string | undefined) => void
    findLastHistoryEntry: (options?: HistoryTrackFnArgs) =>
        | {
              pathname: string
              title: string | undefined
              index: number
              goBackTo: () => void
              navigateTo: () => void
          }
        | undefined
    goBack: (options?: HistoryTrackFnArgs) => void
}

const HistoryTrackerContext = createContext<HistoryTrackerContextType>(null!)

export const HistoryTrackerProvider = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation()
    const navigate = useNavigate()

    const [historyEntries, setHistoryEntries] = useState<
        { pathname: string; title: string | undefined }[]
    >([])

    useEffect(() => {
        // The page title may have already been set, or it may take some time (if there's some kind of
        // loader). Here we pull the page title immediately, but we also allow it to be overridden in
        // overrideLastEntryPageTitle below.
        const titleParts = document.title.split(' | ')
        const title = titleParts.length > 1 ? titleParts.slice(0, -1).join(' | ') : undefined
        setHistoryEntries((prev) =>
            [...prev, { pathname: `${location.pathname}${location.search}`, title }].slice(-100)
        )
    }, [location])

    const overrideLastEntryPageTitle = useCallback((title: string | undefined) => {
        setHistoryEntries((prev) => {
            if (prev.length === 0) {
                return prev
            }
            prev[prev.length - 1].title = title
            return [...prev]
        })
    }, [])

    const findLastHistoryEntry = useCallback(
        (options?: HistoryTrackFnArgs) => {
            const currentEntry = historyEntries.at(-1)
            if (currentEntry === undefined) {
                return undefined
            }

            const nonReportsEntries = rejectReportsEntries(dropRight(historyEntries))

            const index = nonReportsEntries.findLastIndex((entry) => {
                if (entry.pathname === currentEntry.pathname) {
                    return false
                }

                if (options === undefined) {
                    return true
                }

                return !options.excluding.test(entry.pathname)
            })
            if (index === -1) {
                return undefined
            }

            const entry = nonReportsEntries[index]
            return {
                ...entry,
                index,
                goBackTo: () => {
                    setHistoryEntries(nonReportsEntries.slice(0, index))
                    navigate(entry.pathname)
                },
                navigateTo: () => {
                    navigate(entry.pathname)
                },
            }
        },
        [historyEntries]
    )

    const goBack = useCallback(
        (options?: HistoryTrackFnArgs) => {
            const lastEntry = findLastHistoryEntry(options)
            if (lastEntry) {
                setHistoryEntries((prev) => {
                    return prev.slice(0, lastEntry.index)
                })
                navigate(lastEntry.pathname)
            }
        },
        [findLastHistoryEntry, navigate, historyEntries]
    )

    const value = useMemo(
        () => ({ overrideLastEntryPageTitle, findLastHistoryEntry, goBack }),
        [overrideLastEntryPageTitle, findLastHistoryEntry, goBack]
    )

    return <HistoryTrackerContext.Provider value={value}>{children}</HistoryTrackerContext.Provider>
}

export const useHistoryTrackerContext = () => {
    return useContext(HistoryTrackerContext)
}

// Right now we're unable to navigate back to any Reports page but the root, because the
// others require passing data in location state. As a temporary workaround, we're simply
// going to filter out all of those pages.
const rejectReportsEntries = (
    entries: { pathname: string; title: string | undefined }[]
): { pathname: string; title: string | undefined }[] => {
    return entries.filter(({ pathname }) => !pathname.startsWith('/reports/'))
}
